import { Icon } from "@blueprintjs/core";
import { IconName } from "@blueprintjs/icons";
import { differenceInMinutes, isBefore, parseISO } from "date-fns";

import {
  ComponentStatus,
  ComponentTypeEnum,
  MeetingStatus,
  ProgramComponentParticipantsQuery,
  ProgramStatus,
} from "@/graphql";
import {
  CoachingStage,
  ConfigureInput,
  LeadComponent,
  MainComponent,
  MainProgram,
  MeetingPlatforms,
  ParticipantComponent,
} from "@/types/programs";
import { ensureNonNullArray } from "@/utils/array";
import { NonNullableArrayItem } from "@/utils/types";

import {
  getEligibleData,
  isComponentInProgressOrOverdue,
  isComponentUpcomingOrSoon,
} from "./componentMetadata";

// REDEFINED_TYPE: ProgramComponent
type ProgramComponentType =
  | LeadComponent
  | ParticipantComponent
  | MainComponent;

export type DashboardPanelType =
  | "Component"
  | "Participants"
  | "ScheduleLaunchEmail"
  | "AutomaticLaunchEmail";

interface CoachingStageParams {
  completedIntroSessionCount: number;
  upcomingIntroSessionCount: number;
  completedRegularSessionCount: number;
  upcomingRegularSessionCount: number;
}

export const ONE_ONE_COACHING_STAGE_STYLES: Record<
  CoachingStage,
  {
    textColor: string;
    color: string;
    borderColor: string;
    text: string;
    icon?: React.ReactNode;
    iconClass?: IconName;
  }
> = {
  ["NotStarted"]: {
    textColor: "#778397",
    color: "#F5F6F8",
    borderColor: "#b0b6bf",
    text: "Not Started",
  },
  ["IntrosExpired"]: {
    textColor: "#C61D1D",
    color: "#F5F6F8",
    icon: <Icon icon="cross-circle" size={14} color={"#C61D1D"} />,
    iconClass: "cross-circle",
    text: "Intros Expired",
    borderColor: "#C61D1D",
  },
  ["InIntros"]: {
    textColor: "#215DB0",
    color: "#BED9FC",
    icon: <Icon icon="circle" size={14} color={"#215DB0"} />,
    iconClass: "circle",
    text: "In Intros",
    borderColor: "#40B1FF",
  },
  ["CoachPicked"]: {
    textColor: "#215DB0",
    color: "#BED9FC",
    icon: <Icon icon="circle" size={14} color={"#215DB0"} />,
    iconClass: "circle",
    text: "Coach Picked",
    borderColor: "#40B1FF",
  },
  ["SessionActive"]: {
    textColor: "#215DB0",
    color: "#BED9FC",
    icon: <Icon icon="circle" size={14} color={"#215DB0"} />,
    iconClass: "circle",
    text: "Session Active",
    borderColor: "#40B1FF",
  },
  ["Completed"]: {
    textColor: "#129B4C",
    color: "#129B4C",
    icon: <Icon icon="tick-circle" size={14} color={"#0D8050"} />,
    iconClass: "tick-circle",
    text: "Complete",
    borderColor: "#129B4C",
  },
  ["Scheduled"]: {
    textColor: "#215DB0",
    color: "#BED9FC",
    icon: <Icon icon="circle" size={14} color={"#215DB0"} />,
    iconClass: "circle",
    text: "Scheduled",
    borderColor: "#40B1FF",
  },
  ["Missed"]: {
    textColor: "#C61D1D",
    color: "#F5E6E8",
    icon: <Icon icon="cross-circle" size={14} color={"#C61D1D"} />,
    iconClass: "cross-circle",
    text: "Missed",
    borderColor: "#C61D1D",
  },
  ["InCompleted"]: {
    textColor: "#C61D1D",
    color: "#F5E6E8",
    icon: <Icon icon="cross-circle" size={14} color={"#C61D1D"} />,
    iconClass: "cross-circle",
    text: "In Completed",
    borderColor: "#C61D1D",
  },
};

export const isCompleteSubPanel = (
  subPanel: DashboardPanelType,
  program: MainProgram,
): boolean => {
  const completionCheckMap = {
    ["Component"]: () => program?.hasCompletedSetupComponents ?? false,
    ["Participants"]: () => program?.hasParticipants ?? false,
    ["ScheduleLaunchEmail"]: () => program?.scheduleLaunchEmail ?? false,
    ["AutomaticLaunchEmail"]: () => !!program?.sendEnrollmentEmailAt,
  };

  return completionCheckMap[subPanel]?.() ?? false;
};

export function isPanelDefaultVisible(
  program: MainProgram,
  panel: DashboardPanelType,
): boolean {
  switch (panel) {
    case "Component":
      return !program?.hasCompletedSetupComponents;
    case "Participants":
      return Boolean(
        program?.hasCompletedSetupComponents && !program?.hasParticipants,
      );
    case "ScheduleLaunchEmail":
      return Boolean(
        program?.hasCompletedSetupComponents &&
          program?.hasParticipants &&
          !program?.scheduleLaunchEmail,
      );
    case "AutomaticLaunchEmail":
      return false;
    default:
      return false;
  }
}

export const mapComponentToInput = (component: MainComponent) => {
  const formFields = ensureNonNullArray(component.survey?.formFields).map(
    (field) => {
      return {
        id: field.id,
        prompt: field?.prompt,
        order: field?.order,
        answerType: field?.answerType,
        required: field?.required,
        options: ensureNonNullArray(field.formFieldOptions).map((option) => {
          return {
            value: option.value,
            label: option.label,
          };
        }),
        link: field?.link?.url,
        metadata: {
          text: field?.link?.text ?? "",
          imageSrc: field?.link?.imageSrc,
        },
      };
    },
  );

  const sessionChildren = ensureNonNullArray(component?.childComponents).filter(
    (child) => child?.type === ComponentTypeEnum.OneOneCoachingSession,
  );

  const selectedProducts = sessionChildren.map((child) => {
    return child.product?.id || null;
  });

  const roleplayScenarios = ensureNonNullArray(component?.roleplayScenarios);

  const coaches = ensureNonNullArray(component.coaches).map(
    (coach) => coach?.id,
  );

  const participants = ensureNonNullArray(component?.participants).map(
    (participant) => participant.id,
  );

  const selectedScenarioIds = ensureNonNullArray(
    component?.roleplayScenarios,
  ).map((scenario) => scenario?.id);

  const input: ConfigureInput = {
    id: component?.id,
    name: component?.name,
    body: component?.body,
    type: component?.type,
    targetStartDate: component?.targetStartDate,
    targetDueDate: component?.targetDueDate,
    targetEndDate: component?.targetEndDate,
    pendingParticipantEmails: component?.pendingParticipantEmails,
    forAllParticipants: component?.forAllParticipants,
    participantGuidance: component?.participantGuidance,
    visibleUpcomingQuestions: component?.visibleUpcomingQuestions,
    requiredSendLaunchEmail: component?.requiredSendLaunchEmail,
    requiredSendDueEmail: component?.requiredSendDueEmail,
    requiredSendPastDueEmail: component?.requiredSendPastDueEmail,
    prerequisiteComponentId: component?.prerequisiteComponent?.id,
    participantPrelaunchVisibility: component?.participantPrelaunchVisibility,
    autoLaunchWithProgram: component?.autoLaunchWithProgram,
    isReleaseDependentComponents: false,
    specialty: component?.specialty?.id,
    isSetupComplete: component?.isSetupComplete,
    status: component?.status,
    selectedScenarioIds,
    coaches,
    formFields,
    participants,
    roleplayScenarios,
    products: selectedProducts,
    optional: component?.optional,
    scenarioRequiredNum: component?.scenarioRequiredNum,
    scenarioAttemptMin: component?.scenarioAttemptMin,
    scenarioAttemptMax: component?.scenarioAttemptMax,
    scenarioRankMin: component?.scenarioRankMin,
  };

  return input;
};

export const getIntroSessionInput = (component: MainComponent) => {
  const introSession = component.childComponents?.find(
    (child) => child?.type === ComponentTypeEnum.OneOneCoachingIntro,
  );
  if (!introSession) return;

  return {
    id: introSession?.id,
    name: introSession?.name,
    targetStartDate: introSession?.targetStartDate,
    targetDueDate: introSession?.targetDueDate,
    targetEndDate: introSession?.targetEndDate,
    participantGuidance: introSession?.participantGuidance,
    introSessionCount: introSession?.introSessionCount,
    type: ComponentTypeEnum.OneOneCoachingIntro,
  };
};

export const getMeetingSessionInput = (component: MainComponent) => {
  return ensureNonNullArray(component.childComponents)
    ?.filter((child) => child?.type === ComponentTypeEnum.OneOneCoachingSession)
    .map((child) => {
      return {
        id: child?.id,
        name: child?.name,
        targetStartDate: child?.targetStartDate,
        targetDueDate: child?.targetDueDate,
        targetEndDate: child?.targetEndDate,
        participantGuidance: child?.participantGuidance,
        productId: child?.product?.id,
        type: ComponentTypeEnum.OneOneCoachingSession,
        optional: child?.optional,
      };
    });
};

export const DASHBOARD_PANEL_SETTINGS: {
  panel: string;
  subPanels: DashboardPanelType[];
}[] = [
  {
    panel: "Design Your Program",
    subPanels: ["Component", "Participants"],
  },
  {
    panel: "Launch Program",
    subPanels: ["ScheduleLaunchEmail", "AutomaticLaunchEmail"],
  },
];

export interface ExpandBlock {
  expandPanel?: string;
  expandSubPanel?: DashboardPanelType;
}

export const getExpandBlock = (program?: MainProgram): ExpandBlock => {
  if (!program) return {};

  for (const { panel, subPanels } of DASHBOARD_PANEL_SETTINGS) {
    const foundIncompleteSubPanel = subPanels.find(
      (subPanel) => !isCompleteSubPanel(subPanel, program),
    );

    if (foundIncompleteSubPanel) {
      return {
        expandPanel: panel,
        expandSubPanel: foundIncompleteSubPanel,
      };
    }
  }

  return {};
};

export function getSortedComponents<T extends ProgramComponentType>({
  components,
  sortByDependency = false,
}: {
  components: T[];
  sortByDependency?: boolean;
}): T[] {
  const statusOrder: ComponentStatus[] = [
    ComponentStatus.InDesign,
    ComponentStatus.Ongoing,
    ComponentStatus.Upcoming,
    ComponentStatus.Completed,
  ];

  // Create a new array to avoid modifying the original
  return [...components].sort((a, b) => {
    const statusA = a.status;
    const statusB = b.status;
    const orderDiff =
      statusOrder.indexOf(statusA) - statusOrder.indexOf(statusB);

    if (orderDiff !== 0) return orderDiff;

    if (isComponentUpcomingOrSoon(statusA)) {
      return (a.targetStartDate || "").localeCompare(b.targetStartDate || "");
    }

    if (isComponentInProgressOrOverdue(statusA) || statusA === "Completed") {
      const comparison = (a.targetDueDate || "").localeCompare(
        b.targetDueDate || "",
      );

      if (sortByDependency) {
        const eligibleA = getEligibleData(a.prerequisiteComponent);
        const eligibleB = getEligibleData(b.prerequisiteComponent);
        if (eligibleA.isEligible !== eligibleB.isEligible) {
          return eligibleA.isEligible ? -1 : 1;
        } else {
          return isComponentInProgressOrOverdue(statusA)
            ? comparison
            : -comparison;
        }
      }

      return isComponentInProgressOrOverdue(statusA) ? comparison : -comparison;
    }
    return 0;
  });
}
interface IsCloseEarlyComponentProps {
  closedAt?: string | null;
  targetEndDate?: string | null;
}

export function isCloseEarlyComponent({
  closedAt,
  targetEndDate,
}: IsCloseEarlyComponentProps) {
  if (!closedAt || !targetEndDate) return false;

  return isBefore(parseISO(closedAt), parseISO(targetEndDate));
}

interface AllowEditComponentProps {
  component?: LeadComponent | ParticipantComponent | MainComponent;
  isProgramClosed: boolean;
  isProgramLead: boolean;
  program?: MainProgram;
}

export function allowEditComponent({
  component,
  isProgramClosed,
  isProgramLead,
  program,
}: AllowEditComponentProps) {
  if (!component) return false;

  const status = component.status;
  return (
    (status !== "Completed" && isProgramLead && !isProgramClosed) ||
    (!!program &&
      [ProgramStatus.InDesign, ProgramStatus.LaunchReady].includes(
        program.status,
      ))
  );
}

export const canViewComponentStatistic = (
  component: LeadComponent | ParticipantComponent | MainComponent,
  isProgramLead: boolean,
) => {
  const status = component.status;

  return (
    component.type &&
    [
      ComponentTypeEnum.Survey,
      ComponentTypeEnum.ActionItem,
      ComponentTypeEnum.AiRoleplay,
      ComponentTypeEnum.Video,
      ComponentTypeEnum.Article,
    ].includes(component.type) &&
    ["Ongoing", "Completed"].indexOf(status) > -1 &&
    isProgramLead
  );
};

export const getMinDate = (
  dates: (Date | string | null | undefined)[],
  isRemoveFirst = false,
): Date | null => {
  const filteredDates: Date[] = dates
    .filter((_, index) => (isRemoveFirst ? index !== 0 : true))
    .filter((d): d is Date | string => !!d)
    .map((d) => new Date(d));

  if (filteredDates?.length === 0) return null;

  return filteredDates.reduce(
    (min, date) => (date < min ? date : min),
    filteredDates[0],
  );
};

export const getMaxDate = (dates: (Date | string | null | undefined)[]) => {
  const filteredDates = dates
    .filter((date) => date)
    .filter((d): d is Date | string => !!d)
    .map((d) => new Date(d));

  if (filteredDates.length === 0) return null;

  return filteredDates.reduce(
    (max, date) => (date > max ? date : max),
    filteredDates[0],
  );
};

export const getCloseDate = (
  component: LeadComponent | ParticipantComponent | MainComponent,
) => {
  const isCloseEarly = isCloseEarlyComponent(component);
  const { closedAt, targetEndDate } = component || {};
  return isCloseEarly ? closedAt : targetEndDate;
};

export const getDuration = (component: MainComponent) => {
  const { targetStartDate, targetEndDate } = component;

  if (!targetStartDate || !targetEndDate) return 0;

  const startDate = new Date(targetStartDate);
  const endDate = new Date(targetEndDate);

  return differenceInMinutes(endDate, startDate);
};

export function detectMeetingPlatform(url: string): MeetingPlatforms {
  try {
    const urlObj = new URL(url.toLowerCase());

    // Zoom patterns
    if (urlObj.hostname.includes("zoom.us")) {
      return "Zoom";
    }

    // Google Meet patterns
    if (urlObj.hostname.includes("meet.google.com")) {
      return "GoogleMeet";
    }

    // Microsoft Teams patterns
    if (
      urlObj.hostname.includes("teams.microsoft.com") ||
      urlObj.hostname.includes("teams.live.com")
    ) {
      return "MicrosoftTeams";
    }

    return "Others";
  } catch (e) {
    console.error(e);
    return "Others";
  }
}

type Component = NonNullableArrayItem<
  NonNullableArrayItem<
    NonNullable<
      ProgramComponentParticipantsQuery["programComponentParticipants"]
    >["participants"]
  >["programComponents"]
>;

export const calculateRegularSessionStats = (
  programComponents: Component[] = [],
) => {
  const completedRegularSessionCount = programComponents?.reduce(
    (acc, component) => {
      const count =
        component.meetings?.filter(
          (meeting) => meeting.status === MeetingStatus.Complete,
        ).length ?? 0;
      if (
        component.type === ComponentTypeEnum.OneOneCoachingSession &&
        count > 0
      ) {
        return acc + count;
      }
      return acc;
    },
    0,
  );

  const upcomingRegularSessionCount = programComponents?.reduce(
    (acc, component) => {
      const count =
        component.meetings?.filter(
          (meeting) =>
            meeting.status &&
            [
              MeetingStatus.Scheduling,
              MeetingStatus.Soon,
              MeetingStatus.Upcoming,
              MeetingStatus.Ongoing,
            ].includes(meeting.status),
        ).length ?? 0;
      if (
        component.type === ComponentTypeEnum.OneOneCoachingSession &&
        count > 0
      ) {
        return acc + count;
      }
      return acc;
    },
    0,
  );

  const missedRegularSessionCount = programComponents?.reduce(
    (acc, component) => {
      const isMiss = component.status === "Missed";
      if (
        component.type === ComponentTypeEnum.OneOneCoachingSession &&
        isMiss
      ) {
        return acc + 1;
      }
      return acc;
    },
    0,
  );

  return {
    completedRegularSessionCount,
    upcomingRegularSessionCount,
    missedRegularSessionCount,
  };
};

export const calculateIntroStats = (programComponents: Component[] = []) => {
  const completedIntroCount = programComponents?.reduce((acc, component) => {
    const completedIntroCount =
      component.meetings?.filter(
        (meeting) => meeting.status === MeetingStatus.Complete,
      ).length ?? 0;
    if (
      component.type === ComponentTypeEnum.OneOneCoachingIntro &&
      completedIntroCount > 0
    ) {
      return acc + completedIntroCount;
    }
    return acc;
  }, 0);

  const upcomingIntroCount = programComponents?.reduce((acc, component) => {
    const upcomingIntroCount =
      component.meetings?.filter(
        (meeting) =>
          meeting?.status &&
          [
            MeetingStatus.Scheduling,
            MeetingStatus.Soon,
            MeetingStatus.Upcoming,
            MeetingStatus.Ongoing,
          ].includes(meeting.status),
      ).length ?? 0;

    if (
      component.type === ComponentTypeEnum.OneOneCoachingIntro &&
      upcomingIntroCount > 0
    ) {
      return acc + upcomingIntroCount;
    }

    return acc;
  }, 0);

  return {
    completedIntroCount,
    upcomingIntroCount,
  };
};

const isDateExpired = (date: string | null | undefined): boolean => {
  return date ? new Date(date) < new Date() : false;
};

const hasCompletedAllSessions = ({
  completedRegularSessionCount,
  totalSessionComponentCount,
}: {
  completedRegularSessionCount: number;
  totalSessionComponentCount: number;
}): boolean => {
  return completedRegularSessionCount === totalSessionComponentCount;
};

const hasActiveRegularSessionComponents = ({
  upcomingOrOngoingRegularComponentCount,
}: {
  upcomingOrOngoingRegularComponentCount: number;
}): boolean => {
  return upcomingOrOngoingRegularComponentCount > 0;
};

const hasNoSessionActivity = (params: CoachingStageParams): boolean => {
  const {
    completedIntroSessionCount,
    upcomingIntroSessionCount,
    completedRegularSessionCount,
    upcomingRegularSessionCount,
  } = params;

  return (
    !completedIntroSessionCount &&
    !upcomingIntroSessionCount &&
    !completedRegularSessionCount &&
    !upcomingRegularSessionCount
  );
};

type ComponentCoach = NonNullableArrayItem<
  NonNullable<
    ProgramComponentParticipantsQuery["programComponentParticipants"]
  >["participants"]
>["selectedCoach"];

export const getOneOneCoachingStage = ({
  targetProgramComponentEndDate,
  components = [],
  coach,
}: {
  targetProgramComponentEndDate?: string | null;
  components: Component[];
  coach: ComponentCoach;
}): CoachingStage => {
  const introStats = calculateIntroStats(components);
  const regularStats = calculateRegularSessionStats(components);

  const introComponent = components?.filter(
    (component) => component.type === ComponentTypeEnum.OneOneCoachingIntro,
  )[0];

  const upcomingIntroSessionCount = introStats.upcomingIntroCount || 0;
  const completedIntroSessionCount = introStats.completedIntroCount || 0;
  const upcomingRegularSessionCount =
    regularStats.upcomingRegularSessionCount || 0;
  const completedRegularSessionCount =
    regularStats.completedRegularSessionCount || 0;

  const upcomingOrOngoingRegularComponentCount = components.filter(
    (component) =>
      component.type === ComponentTypeEnum.OneOneCoachingSession &&
      (component.status === ComponentStatus.Upcoming ||
        component.status === ComponentStatus.Soon ||
        component.status === ComponentStatus.Ongoing),
  ).length;

  const isIntroExpired = isDateExpired(introComponent?.targetEndDate);
  const hasCompletedIntro = completedIntroSessionCount > 0;
  const isProgramComponentExpired = isDateExpired(
    targetProgramComponentEndDate,
  );

  // Not Started
  if (
    hasNoSessionActivity({
      completedIntroSessionCount,
      upcomingIntroSessionCount,
      completedRegularSessionCount,
      upcomingRegularSessionCount,
    }) &&
    !isIntroExpired &&
    !isProgramComponentExpired &&
    !coach
  ) {
    return "NotStarted";
  }

  // Intros Expired
  if (!hasCompletedIntro && isIntroExpired && !isProgramComponentExpired) {
    return "IntrosExpired";
  }

  // In Intros
  if (!hasCompletedIntro && upcomingIntroSessionCount > 0) {
    return "InIntros";
  }

  // In Completed (Program component  expired)
  if (!hasCompletedIntro && isProgramComponentExpired) {
    return "InCompleted";
  }

  // For stages requiring completed intro
  if (hasCompletedIntro) {
    // Completed some Intros but no coach picked
    if (!coach) {
      return "InIntros";
    }

    // Complete
    const hasCompletedIntroSession = completedIntroSessionCount > 0;
    if (
      hasCompletedAllSessions({
        completedRegularSessionCount:
          completedRegularSessionCount + (hasCompletedIntroSession ? 1 : 0),
        totalSessionComponentCount: components.length,
      })
    ) {
      return "Completed";
    }

    // Session Active
    if (
      hasActiveRegularSessionComponents({
        upcomingOrOngoingRegularComponentCount,
      }) &&
      !isProgramComponentExpired
    ) {
      return "SessionActive";
    }

    // Coach Picked
    if (coach && !isProgramComponentExpired) {
      return "CoachPicked";
    }

    // In Completed (Program expired)
    if (isProgramComponentExpired) {
      return "InCompleted";
    }
  }

  // Default case
  return "NotStarted";
};

export const roundToOneDecimalPlace = (
  value: number | string,
  suffix?: string,
): string => {
  return (
    Number(value ?? 0)
      .toFixed(1)
      .replace(/\.0$/, "") + (suffix ?? "")
  );
};
